import { Badge, Box, Stack, Tabs, Text } from '@chakra-ui/react'
import { useStore } from '@tanstack/react-store'
import { PrivateBox } from 'features/Private/Private'
import isEmpty from 'lodash/isEmpty'
import { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import {
  PAYMENT_STATE,
  PAYMENT_TYPE,
  PAYMENT_TYPE_LABEL,
  PAYMENT_TYPE_SOURCE_LABEL,
  PAYMENT_STATE_COLOR,
  PAYMENT_STATE_LABEL,
} from 'shared/constants/payments'
import { ROLES } from 'shared/constants/user'
import { authStore } from 'shared/store/auth'
import { getDateTimeReadFormatMoscow } from 'shared/utils/dateFormatting'
import {
  CardContainer,
  Container,
  ContentContainer,
  ContentContainerColumn,
  GridField,
  GridFieldHeading,
  GridFieldItem,
  RightAside,
} from 'uikit/layout'
import { rbNullDistinationTitle } from '../Payments.constants'
import { CompletePaymentModal } from './CompletePaymentModal/CompletePaymentModal'
import { FinishedPaymentModal } from './FinishedPaymentModal/FinishedPaymentModal'
import { HistoryList } from './HistoryList/HistoryList'
import { useFetchPayment } from './hooks/useFetchPayment'

export const PaymentsCard = () => {
  const history = useHistory()
  const { id, paramTabIndex } = useParams()
  const { user } = useStore(authStore)
  const { data: item = {}, isFetching: loading } = useFetchPayment(id)
  const isReadyItem = !isEmpty(item)
  const [tabIndex, setTabIndex] = useState(Number(paramTabIndex) || 0)

  const handleBack = () => {
    history.goBack()
  }

  useEffect(() => {
    setTabIndex(Number(paramTabIndex))
  }, [paramTabIndex])

  const tabValues = ['payment-data', 'history']

  const handleTabsChange = (details) => {
    const index = tabValues.indexOf(details.value)
    history.replace(`/payments/show/${index}/${id}`)
  }

  const distination = `${item?.destinationAmount ?? ''} ${item?.destinationCurrency ?? ''}`
  const distinationRender =
    item.destinationAmount === 0 && item.paymentType === PAYMENT_TYPE.RF_RB_TRANSFER_TO_CARD
      ? rbNullDistinationTitle
      : distination

  return (
    <Container>
      <CardContainer columns="86px 1fr" indentTop={70} onBack={handleBack}>
        <Tabs.Root
          variant="outline"
          colorPalette="orange"
          onValueChange={handleTabsChange}
          value={tabValues[tabIndex]}
        >
          <Tabs.List>
            <Tabs.Trigger value="payment-data">Данные платежа</Tabs.Trigger>
            <Tabs.Trigger value="history">История</Tabs.Trigger>
          </Tabs.List>

          <Tabs.Content value="payment-data">
            <ContentContainer>
              <ContentContainerColumn>
                <GridField>
                  <GridFieldHeading>Данные перевода</GridFieldHeading>

                  {item.paymentType === PAYMENT_TYPE.QR_PAY_IDRAM && (
                    <>
                      <GridFieldItem
                        label="Название организации"
                        isLoading={loading}
                        isReady={isReadyItem}
                      >
                        {item?.finOrgName}
                      </GridFieldItem>

                      <GridFieldItem
                        label="Тин организации"
                        isLoading={loading}
                        isReady={isReadyItem}
                      >
                        {item?.tin}
                      </GridFieldItem>

                      <GridFieldItem label="МСС" isLoading={loading} isReady={isReadyItem}>
                        {item?.mcc}
                      </GridFieldItem>
                    </>
                  )}

                  {![PAYMENT_TYPE.SBP_TOPUP_QR, PAYMENT_TYPE.SBP_TOPUP_TRANSFER].includes(
                    item?.paymentType
                  ) && (
                    <GridFieldItem label="Сумма списания" isLoading={loading} isReady={isReadyItem}>
                      {`${item?.amount} ${item?.currency}`}
                    </GridFieldItem>
                  )}

                  {![
                    PAYMENT_TYPE.SBP_TOPUP_QR,
                    PAYMENT_TYPE.SBP_TOPUP_TRANSFER,
                    PAYMENT_TYPE.ACQUIRING_ONLY,
                  ].includes(item?.paymentType) && (
                    <GridFieldItem label="Сумма комиссии" isLoading={loading} isReady={isReadyItem}>
                      {`${item?.commission ?? ''} ${item?.commissionCurrency ?? ''}`}
                    </GridFieldItem>
                  )}

                  {![
                    PAYMENT_TYPE.SBP_TOPUP_QR,
                    PAYMENT_TYPE.SBP_TOPUP_TRANSFER,
                    PAYMENT_TYPE.ACQUIRING_ONLY,
                  ].includes(item?.paymentType) && (
                    <GridFieldItem
                      label="Сумма к выдаче получателю"
                      isLoading={loading}
                      isReady={isReadyItem}
                    >
                      {distinationRender}
                    </GridFieldItem>
                  )}

                  <GridFieldItem label="Тип" isLoading={loading} isReady={isReadyItem}>
                    {PAYMENT_TYPE_LABEL[item?.paymentType] || item.paymentType}
                  </GridFieldItem>

                  {[
                    PAYMENT_TYPE.RF_RA_TRANSFER_TO_CARD,
                    PAYMENT_TYPE.RF_RB_TRANSFER_TO_CARD,
                  ].includes(item?.paymentType) && (
                    <>
                      <GridFieldItem
                        label="ФИО получателя"
                        isLoading={loading}
                        isReady={isReadyItem}
                      >
                        {item?.payeeFIO}
                      </GridFieldItem>
                    </>
                  )}

                  {item?.paymentDestination && PAYMENT_TYPE.QR_PAY_IDRAM !== item.paymentType && (
                    <GridFieldItem
                      label="Карта получателя"
                      to={
                        !!item?.paymentDestination &&
                        `/payments/payeeCardNumber=${item?.paymentDestination}`
                      }
                      isLoading={loading}
                      isReady={isReadyItem}
                      transition="0.2s"
                    >
                      {item?.paymentDestination}
                    </GridFieldItem>
                  )}

                  {![
                    PAYMENT_TYPE.SBP_TOPUP_QR,
                    PAYMENT_TYPE.SBP_TOPUP_TRANSFER,
                    PAYMENT_TYPE.ACQUIRING_ONLY,
                    PAYMENT_TYPE.QR_PAY_IDRAM,
                  ].includes(item?.paymentType) &&
                    item.paymentType && (
                      <GridFieldItem
                        to={!!item?.payeePhone && `/user/phone=${item?.payeePhone}`}
                        label="Получатель"
                        isLoading={loading}
                        isReady={isReadyItem}
                      >
                        {item?.payeePhone}
                      </GridFieldItem>
                    )}

                  {[PAYMENT_TYPE.WITHDRAW_TO_CARD].includes(item.paymentType) && (
                    <>
                      <GridFieldItem label="Номер карты" isLoading={loading} isReady={isReadyItem}>
                        {!!item?.panMask ? '******' + item?.panMask : undefined}
                      </GridFieldItem>
                      <GridFieldItem
                        label="Банк получателя"
                        isLoading={loading}
                        isReady={isReadyItem}
                      >
                        {!!item?.bankName ? item?.bankName : undefined}
                      </GridFieldItem>
                    </>
                  )}

                  {[PAYMENT_TYPE.SBP_TOPUP_QR, PAYMENT_TYPE.SBP_TOPUP_TRANSFER].includes(
                    item.paymentType
                  ) && (
                    <GridFieldItem
                      label="Банк отправителя"
                      isLoading={loading}
                      isReady={isReadyItem}
                    >
                      {!!item?.bankName ? item?.bankName : undefined}
                    </GridFieldItem>
                  )}

                  {[PAYMENT_TYPE.SBP_TOPUP_QR, PAYMENT_TYPE.SBP_TOPUP_TRANSFER].includes(
                    item?.paymentType
                  ) && (
                    <GridFieldItem
                      label="Счет зачисления"
                      isLoading={loading}
                      isReady={isReadyItem}
                    >
                      {item?.recipientAccountNumber}
                    </GridFieldItem>
                  )}

                  {[PAYMENT_TYPE.SBP_TOPUP_QR, PAYMENT_TYPE.SBP_TOPUP_TRANSFER].includes(
                    item?.paymentType
                  ) && (
                    <GridFieldItem label="Сумма пополнения" isLoading={loading} isReady={isReadyItem}>
                      {`${item?.amount} ${item?.currency}`}
                    </GridFieldItem>
                  )}

                  {[PAYMENT_TYPE.SBP_TOPUP_QR, PAYMENT_TYPE.SBP_TOPUP_TRANSFER].includes(
                    item?.paymentType
                  ) && (
                    <GridFieldItem
                      label="Идентификатор операции СБП"
                      isLoading={loading}
                      isReady={isReadyItem}
                    >
                      {item?.sbpOperId}
                    </GridFieldItem>
                  )}

                  {![PAYMENT_TYPE.SBP_TOPUP_QR, PAYMENT_TYPE.SBP_TOPUP_TRANSFER].includes(
                    item?.paymentType
                  ) && (
                    <GridFieldItem
                      to={!!item?.payerPhone && `/user/phone=${item?.payerPhone}`}
                      label="Отправитель"
                      isLoading={loading}
                      isReady={isReadyItem}
                    >
                      {item?.payerPhone}
                    </GridFieldItem>
                  )}

                  {[PAYMENT_TYPE.SBP_TOPUP_QR, PAYMENT_TYPE.SBP_TOPUP_TRANSFER].includes(
                    item?.paymentType
                  ) && (
                    <GridFieldItem label="Отправитель" isLoading={loading} isReady={isReadyItem}>
                      {item?.payerPam}
                    </GridFieldItem>
                  )}

                  <GridFieldItem
                    to={!!item?.payerPhone && `/user/phone=${item?.payerPhone}`}
                    label="Номер телефона отправителя"
                    isLoading={loading}
                    isReady={isReadyItem}
                  >
                    {item?.payerPhone}
                  </GridFieldItem>

                  <GridFieldItem label="Дата (по Мск)" isLoading={loading} isReady={isReadyItem}>
                    {getDateTimeReadFormatMoscow(item?.paymentDate)}
                  </GridFieldItem>

                  <GridFieldItem label="Часовой пояс" isLoading={loading} isReady={isReadyItem}>
                    {!!item?.userTimeZone ? item?.userTimeZone : undefined}
                  </GridFieldItem>

                  <GridFieldItem label="PaymentID" isLoading={loading} isReady={isReadyItem}>
                    {item?.id}
                  </GridFieldItem>

                  {![PAYMENT_TYPE.SBP_TOPUP_QR, PAYMENT_TYPE.SBP_TOPUP_TRANSFER].includes(
                    item?.paymentType
                  ) && (
                    <GridFieldItem label="RRN" isLoading={loading} isReady={isReadyItem}>
                      {item?.rrn}
                    </GridFieldItem>
                  )}

                  {![PAYMENT_TYPE.SBP_TOPUP_QR, PAYMENT_TYPE.SBP_TOPUP_TRANSFER].includes(
                    item?.paymentType
                  ) && (
                    <GridFieldItem
                      label={`Источник (${PAYMENT_TYPE_SOURCE_LABEL[item.paymentSourceType]})`}
                      isLoading={loading}
                      isReady={isReadyItem}
                    >
                      {item.paymentSource
                        ? item.paymentSource
                        : item.paymentType === 'WITHDRAW_TO_CARD'
                          ? 'Внутренний счёт'
                          : item.paymentSource}
                    </GridFieldItem>
                  )}

                  {item.state === PAYMENT_STATE.NOT_ACQUIRED && (
                    <>
                      <GridFieldItem label="Код ошибки" isLoading={loading} isReady={isReadyItem}>
                        {item?.subStatusCode}
                      </GridFieldItem>
                      <GridFieldItem
                        label="Расшифровка ошибки"
                        isLoading={loading}
                        isReady={isReadyItem}
                      >
                        {item?.subStatusDescription}
                      </GridFieldItem>
                    </>
                  )}
                </GridField>

                {item?.retryComment && (
                  <GridField>
                    <GridFieldHeading>Комментарий</GridFieldHeading>
                    <GridFieldItem isLoading={loading} isReady={isReadyItem}>
                      {item?.retryComment}
                    </GridFieldItem>
                  </GridField>
                )}

                {(item.state === 'MANUAL_CHECK' || item.state === 'ROLLBACK_WITH_REFUND') &&
                  item?.description && (
                    <GridField>
                      <GridFieldHeading>Ошибка платежа</GridFieldHeading>
                      <GridFieldItem isLoading={loading} isReady={isReadyItem}>
                        {item?.description}
                      </GridFieldItem>
                    </GridField>
                  )}
              </ContentContainerColumn>
              <ContentContainerColumn>
                <Box position="sticky" top="10px">
                  <RightAside>
                    <Text fontSize="0.82rem" color="gray.400" mb={2}>
                      Статус
                    </Text>
                    <Badge
                      minH="40px"
                      padding="1px 10px"
                      borderRadius={5}
                      fontSize="1rem"
                      width="100%"
                      justifyContent="center"
                      pt={3}
                      pb={3}
                      whiteSpace="none"
                      colorPalette={PAYMENT_STATE_COLOR[item.state] || 'gray'}
                      userSelect="text"
                    >
                      {PAYMENT_STATE_LABEL[item.state] || item.state}
                    </Badge>
                    {item.state === PAYMENT_STATE.MANUAL_CHECK &&
                      ![
                        PAYMENT_TYPE.RF_RB_TRANSFER_TO_CARD,
                        PAYMENT_TYPE.UZ_TRANSFER_BY_PHONE,
                        PAYMENT_TYPE.UZ_TRANSFER_TO_CARD,
                      ].includes(item.paymentType) && (
                        <PrivateBox hasAnyRole={[ROLES.admin]}>
                          <Stack gap={2} pt="20px">
                            <CompletePaymentModal id={id} />
                          </Stack>
                        </PrivateBox>
                      )}

                    {[
                      PAYMENT_STATE.MANUAL_CHECK,
                      PAYMENT_STATE.FINISHED_WITH_ERRORS,
                      PAYMENT_STATE.ACQUIRING_CANCEL_ERROR,
                    ].includes(item.state) &&
                      ![
                        PAYMENT_TYPE.RF_RB_TRANSFER_TO_CARD,
                        PAYMENT_TYPE.UZ_TRANSFER_BY_PHONE,
                        PAYMENT_TYPE.UZ_TRANSFER_TO_CARD,
                      ].includes(item.paymentType) && (
                        <PrivateBox hasAnyRole={[ROLES.admin]}>
                          <Stack gap={2} pt="20px">
                            <FinishedPaymentModal id={id} />
                          </Stack>
                        </PrivateBox>
                      )}
                  </RightAside>
                </Box>
              </ContentContainerColumn>
            </ContentContainer>
          </Tabs.Content>
          <Tabs.Content value="history">
            <HistoryList id={id} />
          </Tabs.Content>
        </Tabs.Root>
      </CardContainer>
    </Container>
  )
}
